import * as Sentry from "@sentry/react";
import React from "react";
import {
  createRoutesFromChildren,
  matchRoutes,
  useLocation,
  useNavigationType,
} from "react-router-dom";
import { isReplayAllowedPath, scrubBreadcrumb, scrubEvent } from "./utils/replayPolicy";

// Envelopes go through our backend instead of *.ingest.sentry.io, so
// ad-blockers can't drop crash reports (net::ERR_BLOCKED_BY_CLIENT).
// REACT_APP_API_URL may end in "/api/" or "/api/v1" — normalize to the bare
// "/api" root before appending the tunnel path.
const apiRoot = (process.env.REACT_APP_API_URL || "http://localhost:8000/api/")
  .replace(/\/+$/, "")
  .replace(/\/v1$/, "");

// Session replay records what people do on screen — on this platform that
// is sensitive. Two gates, both must pass:
//   1. analytics consent (Cookie Policy §2.3a) — without it, errors only;
//   2. the route allow-list in utils/replayPolicy — questionnaire, recordings,
//      workbook, team and profile pages never record (Sentry DPA §2.2.2).
const hasAnalyticsConsent = () => {
  try {
    return JSON.parse(localStorage.getItem("cookie_consent"))?.analytics === true;
  } catch {
    return false;
  }
};

const currentPath = () => (typeof window !== "undefined" ? window.location.pathname : "/");

let consentGranted = hasAnalyticsConsent();
let integrationAdded = false;
let recording = false;

// Held here instead of Sentry.getReplay() - the static getReplay import
// would pull rrweb (~63KB raw) back into main.js despite the lazy load.
let replayRef;
const replayInstance = () => replayRef;

// Adds the integration once. Sentry starts it in "buffer" or "session" mode
// per the sample rates below; we only ever add it on an allowed route.
function addReplayIntegration() {
  if (integrationAdded) return;
  integrationAdded = true;
  // lazyLoadIntegration keeps rrweb (~26KB gz) out of main.js; the replay
  // code is fetched from Sentry's CDN only when consent + route allow it.
  Sentry.lazyLoadIntegration("replayIntegration")
    .then((replayIntegration) => {
      replayRef = replayIntegration({ maskAllText: true, blockAllMedia: true });
      Sentry.addIntegration(replayRef);
      recording = true;
    })
    .catch(() => {
      // CDN blocked (ad-blocker/CSP) - errors still report, replay just stays off.
      integrationAdded = false;
    });
}

/**
 * Reconcile replay with the current route. Called on every navigation.
 * Leaving an allowed route stops recording and discards the buffer; coming
 * back resumes in buffer mode (sent only if an error occurs), which is the
 * more conservative of Sentry's two modes.
 */
export function syncReplayForPath(pathname = currentPath()) {
  const allowed = isReplayAllowedPath(pathname);
  if (!consentGranted) return;

  if (!integrationAdded) {
    if (allowed) addReplayIntegration();
    return;
  }

  const replay = replayInstance();
  if (!replay) return;
  if (!allowed && recording) {
    recording = false;
    Promise.resolve(replay.stop()).catch(() => {});
  } else if (allowed && !recording) {
    recording = true;
    try {
      replay.startBuffering();
    } catch {
      /* already running */
    }
  }
}

/** Consent granted (cookie banner) — start replay if the route permits. */
export function enableSessionReplay() {
  consentGranted = true;
  syncReplayForPath();
}

/** Consent withdrawn — stop recording immediately and stay stopped. */
export function disableSessionReplay() {
  consentGranted = false;
  const replay = replayInstance();
  if (replay && recording) {
    recording = false;
    Promise.resolve(replay.stop()).catch(() => {});
  }
}

// index.html ships no executable inline script — all our code runs from
// /static/js/*.js. An error whose every frame sits in the HTML document
// itself ("global code" at /register:1:3) came from script injected by an
// in-app browser, extension or content blocker, not from us. Drop it.
const isInjectedScriptError = (event) => {
  const frames = (event?.exception?.values || []).flatMap(
    (value) => value?.stacktrace?.frames || []
  );
  if (frames.length === 0) return false;
  return frames.every((frame) => {
    const file = (frame.filename || frame.abs_path || "").split(/[?#]/)[0];
    return file !== "" && !/\.m?js$/.test(file);
  });
};

Sentry.init({
  dsn: process.env.REACT_APP_SENTRY_DSN,
  environment: process.env.NODE_ENV,
  release: process.env.REACT_APP_VERSION || "1.0.0",
  sendDefaultPii: false,
  tunnel: `${apiRoot}/v1/monitoring/envelope/`,

  // Nothing member-authored leaves in an error report: no payloads, query
  // strings, cookies, console output or email — see utils/replayPolicy.
  beforeSend: (event) => (isInjectedScriptError(event) ? null : scrubEvent(event)),
  beforeBreadcrumb: scrubBreadcrumb,

  integrations: [
    // React Router v6 tracing — captures page load & navigation spans
    Sentry.reactRouterV6BrowserTracingIntegration({
      useEffect: React.useEffect,
      useLocation,
      useNavigationType,
      createRoutesFromChildren,
      matchRoutes,
    }),
  ],

  // Performance: 10% of transactions in production
  tracesSampleRate: process.env.NODE_ENV === "production" ? 0.1 : 1.0,

  // Propagate tracing headers to your backend API
  tracePropagationTargets: [
    "localhost",
    /^https:\/\/.*\.up\.railway\.app/,
  ],

  // Session Replay: capture 10% of sessions, 100% of error sessions
  replaysSessionSampleRate: 0.1,
  replaysOnErrorSampleRate: 1.0,
});

// Initial load: attach replay only if consent exists AND the landing route
// is allowed. Route changes are reconciled from App via syncReplayForPath.
syncReplayForPath();
